/**
 * プレイ画面(docs/02_game_design.md §3)。
 *
 * ┌─────────────────────────────────────────┐
 * │  スコア(+加算ポップ)  コンボ数  コンボゲージ │
 * │        [画像]     ♪音声再生                │
 * │      ふりがな(ルビ)※日本語のみ             │
 * │      問題文(漢字/英単語)                   │
 * │      ローマ字ガイド(難易度により表示制御)     │
 * │  ┌─────────────────────────────┐          │
 * │  │ 色分けキーボード(JIS)+ 指の透過表示 │        │
 * │  └─────────────────────────────┘          │
 * └─────────────────────────────────────────┘
 *
 * Phaser は背景レイヤのみ。文字・HUD・キーボードは DOM/SVG で描く(docs/03 §4)。
 */

import {
  DirectValidator,
  RomajiValidator,
  TypingSession,
  getDifficultySettings,
  type InputValidator,
} from '@paltyping/engine';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Emitter } from '../bridge/emitter';
import { PlayController, type PlayEffect } from '../bridge/playController';
import { createEffectSoundPlayer } from '../bridge/playSound';
import { resolveGuideVisibility, resolveWordPresentation } from '../logic/guide';
import { learningRecorder } from '../services/learning';
import { sound } from '../services/sound';
import { TYPING_CHEER_MS, typingCheerAt } from '../services/tutorSupport';
import { useGameStore } from '../store/gameStore';
import { AvatarView } from './components/AvatarView';
import { CursorChars } from './components/CursorText';
import { FitText } from './components/FitText';
import { HandsOverlay } from './components/HandsOverlay';
import { KeyboardGuide } from './components/KeyboardGuide';
import { RomajiGuide } from './components/RomajiGuide';
import { ScorePops } from './components/ScorePops';
import { TutorBubble } from './components/TutorBubble';
import { PhaserBackground } from './PhaserBackground';
import { useViewport } from './useViewport';

const COUNTDOWN_STEP_MS = 700;

function formatTime(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function PlayScreen() {
  const bundle = useGameStore((state) => state.bundle);
  const bundleStatus = useGameStore((state) => state.bundleStatus);
  const language = useGameStore((state) => state.language);
  /**
   * 開始時の条件。同期で設定・装備が更新されても、実行中controllerを作り直さない。
   * 次プレイでは gameStore.startStage が最新値で作り直す(docs/04 §5.1.3)。
   */
  const playConditions = useGameStore((state) => state.playConditions);
  const play = useGameStore((state) => state.play);
  const setPlaySnapshot = useGameStore((state) => state.setPlaySnapshot);
  const finishSession = useGameStore((state) => state.finishSession);
  const backToStageSelect = useGameStore((state) => state.backToStageSelect);
  const catalog = useGameStore((state) => state.catalog);
  const inventory = useGameStore((state) => state.inventory);
  const practice = useGameStore((state) => state.practice);
  /** 「パルの おてつだい」(docs/07 §7.4)。オフなら応援も出さない。 */
  const palHelp = useGameStore((state) => state.settings.palHelp);

  const effectsRef = useRef<Emitter<PlayEffect> | null>(null);
  effectsRef.current ??= new Emitter<PlayEffect>();
  const effects = effectsRef.current;

  const [countdown, setCountdown] = useState(3);
  // 文章ステージ(最長35かな/45文字)を画面に収めるための実寸(docs/02 §3)。
  const viewport = useViewport();
  const difficulty = playConditions?.difficulty;
  const firstLanguage = playConditions?.firstLanguage;
  const settings = useMemo(
    () => (difficulty ? getDifficultySettings(difficulty) : null),
    [difficulty],
  );

  // カウントダウン(アセット無しでも「よーい どん」の間を作る)。
  useEffect(() => {
    if (bundleStatus !== 'ready') return;
    setCountdown(3);
    const timers = [1, 2, 3].map((step) =>
      setTimeout(() => setCountdown(3 - step), step * COUNTDOWN_STEP_MS),
    );
    return () => timers.forEach(clearTimeout);
  }, [bundleStatus, bundle]);

  // セッション本体。カウントダウン終了で開始する。
  useEffect(() => {
    if (!bundle || !playConditions || countdown > 0) return;
    const validator: InputValidator =
      language === 'japanese' ? new RomajiValidator() : new DirectValidator();
    const session = new TypingSession({
      words: bundle.words,
      validator,
      difficulty: playConditions.difficulty,
      // コイン係数が言語別(docs/02 §5.7)なので、見積りにも言語を渡す。
      language,
      quiz: bundle.stage.quiz,
      modifiers: playConditions.modifiers,
    });
    const controller = new PlayController({
      session,
      bundle,
      language,
      firstLanguage: playConditions.firstLanguage,
      onSnapshot: setPlaySnapshot,
      onEnd: (result) => {
        void finishSession(result);
      },
      // 学習イベント記録(docs/07_learning_support.md §2)。セッション終了時に圧縮保存される。
      learning: learningRecorder.sink({
        stageId: bundle.stage.id,
        stageTitle: bundle.stage.title,
        difficulty: playConditions.difficulty,
        language,
        practice: practice !== null,
      }),
    });
    // 出題時・打ち終わり時の単語音声もここ(bridge/playSound.ts)に集約している。
    // 難易度を渡すのは、英語 ふつう/むずかしい で「打ち終わりの答え合わせ再生」をしないため。
    const playSound = createEffectSoundPlayer(sound, language, playConditions.difficulty);
    const off = controller.effects.on((effect) => {
      effects.emit(effect);
      playSound(effect);
    });
    controller.start();
    return () => {
      off();
      controller.stop();
      // 画面を離れたあとに単語音声が鳴り続けないようにする。
      sound.stopVoice();
    };
  }, [
    bundle,
    countdown,
    language,
    playConditions,
    practice,
    setPlaySnapshot,
    finishSession,
    effects,
  ]);

  // Esc でリタイア。
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') backToStageSelect();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [backToStageSelect]);

  if (bundleStatus !== 'ready' || !bundle || !settings || !difficulty || !firstLanguage) {
    return (
      <div className="screen play loading">
        <p className="loading-text">よみこみちゅう…</p>
      </div>
    );
  }

  const stage = play?.performanceStage ?? 'normal';
  const word = play?.word ?? null;
  const guide = resolveGuideVisibility({
    settings,
    language,
    wordElapsedMs: play?.wordElapsedMs ?? 0,
    idleMs: play?.idleMs ?? 0,
    maxIdleMs: play?.maxIdleMs ?? 0,
    missedInWord: play?.missedInWord ?? false,
    hasTypedInWord: play?.hasTypedInWord ?? false,
  });
  const presentation = resolveWordPresentation({ settings, language });
  const gauge = play?.gauge ?? 0;
  const total = play?.totalQuestions ?? null;
  // ふりがな(や、ひらがなだけの問題文)のいま打つ位置。読みと同じ文字列のときだけ引ける。
  const readingIndex = play?.unitIndex ?? 0;
  const rubyHasCursor = word?.ruby != null && word.ruby === word.reading;
  const displayIsReading = word != null && word.display === word.reading;
  // 下線(.cursor-bar)を「次の出題ではワープ、同じ出題の中では横スライド」に切り替えるための鍵。
  // 同じ語が続けて出ることがあるので、出題番号も混ぜて必ず変わるようにする。
  const wordKey = `${word?.id ?? ''}#${play?.completedQuestions ?? 0}`;

  /**
   * 手が止まっている子への応援(docs/07 §7.4)。
   *
   * **定型のローテだけ**で LLM は呼ばない(打鍵中に通信を挟まない)。
   * 「つぎに打つキー」のヒントは既存の指ガイドが担っているので、ここは
   * 気持ちの後押しだけ。カウントダウン中は出さない(まだ打っていないので
   * 「止まっている」ではない)。20秒ごとにことばが変わる。
   */
  const idleMs = play?.idleMs ?? 0;
  const cheerRound = Math.floor(idleMs / TYPING_CHEER_MS);
  const cheer =
    palHelp && countdown <= 0 && cheerRound >= 1
      ? typingCheerAt(cheerRound - 1 + (play?.completedQuestions ?? 0))
      : null;

  return (
    <div className={`screen play stage-${stage}`}>
      <PhaserBackground stage={stage} effects={effects} />

      <header className="hud">
        <div className="hud-block score-block">
          <span className="hud-label">スコア</span>
          <span className="hud-value score">{play?.score ?? 0}</span>
          <ScorePops effects={effects} />
        </div>
        <div className="hud-block combo-block">
          <span className="hud-label">コンボ</span>
          <span className={`hud-value combo ${(play?.combo ?? 0) >= 10 ? 'hot' : ''}`}>
            {play?.combo ?? 0}
          </span>
        </div>
        <div className="hud-block gauge-block">
          <div className="gauge-top">
            <span className="hud-label">ゲージ</span>
            <span className={`multiplier m${play?.gaugeMultiplier ?? 1}`}>
              ×{play?.gaugeMultiplier ?? 1}
            </span>
          </div>
          <div className="gauge-track">
            <div className={`gauge-fill ${stage}`} style={{ width: `${gauge}%` }} />
          </div>
          <span className={`stage-badge ${stage}`}>
            {stage === 'fever' ? 'フィーバー!!' : stage === 'groove' ? 'ノリノリ!' : ''}
          </span>
        </div>
        <div className="hud-block progress-block">
          <span className="hud-label">{play?.remainingMs != null ? 'のこり' : 'もんだい'}</span>
          <span className="hud-value">
            {play?.remainingMs != null
              ? formatTime(play.remainingMs)
              : `${Math.min((play?.completedQuestions ?? 0) + 1, total ?? 1)} / ${total ?? '-'}`}
          </span>
        </div>
        <div className="hud-block avatar-block">
          <AvatarView catalog={catalog} inventory={inventory} size="sm" />
        </div>
        <button type="button" className="quit-button" onClick={backToStageSelect}>
          やめる
        </button>
      </header>

      <section className="word-panel">
        {word?.imageUrl ? (
          <img
            className="word-image"
            src={word.imageUrl}
            alt=""
            // 機能語・数詞など「画像なし運用」の語や未生成の語では 404 になる。
            // 壊れた画像アイコンを見せず、そのまま非表示にする(docs/05 §2)。
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : null}
        {word?.audioUrl ? (
          <button
            type="button"
            className="voice-button"
            onClick={() => sound.playVoice(word.audioUrl)}
          >
            ♪ もういちど きく
          </button>
        ) : null}

        {word?.ruby && presentation.showWordText ? (
          <FitText role="ruby" measureText={word.ruby} viewport={viewport} className="word-ruby">
            {rubyHasCursor ? (
              <CursorChars text={word.ruby} activeIndex={readingIndex} resetKey={wordKey} />
            ) : (
              word.ruby
            )}
          </FitText>
        ) : null}

        {presentation.showWordText ? (
          <FitText
            role="display"
            measureText={word?.display ?? ''}
            viewport={viewport}
            className="word-display"
          >
            {/* ひらがなだけの問題文(ドリル)は、それ自体が読みなので下線を引く。 */}
            {word && displayIsReading ? (
              <CursorChars text={word.display} activeIndex={readingIndex} resetKey={wordKey} />
            ) : (
              (word?.display ?? '')
            )}
          </FitText>
        ) : null}

        {presentation.showMeaningPrompt && word?.meaning ? (
          <FitText
            role="meaning"
            measureText={`「${word.meaning}」を えいごで!`}
            viewport={viewport}
            className="word-meaning"
          >
            「{word.meaning}」を えいごで!
          </FitText>
        ) : null}
        {/* 英語かんたん: 飾り無しで訳だけを、ふつうと同じ位置・デザインで出す。 */}
        {presentation.showMeaningPlain && word?.meaning ? (
          <FitText
            role="meaning"
            measureText={word.meaning}
            viewport={viewport}
            className="word-meaning"
          >
            {word.meaning}
          </FitText>
        ) : null}
        {presentation.showWordText && language === 'japanese' && word?.meaning ? (
          <p className="word-meaning small">{word.meaning}</p>
        ) : null}

        <RomajiGuide
          typed={play?.typed ?? ''}
          remaining={play?.remaining ?? ''}
          mode={guide.romaji}
          hintActive={guide.hintActive}
          viewport={viewport}
          wordKey={wordKey}
        />
      </section>

      {/*
        キーボードの領域は**常に確保**する(出し入れで上の文字が動かないように)。
        隠すときは中身だけ消す。1366x768 の縦の割り当ては styles.css の .keyboard-panel を参照。
      */}
      <section
        className={`keyboard-panel ${guide.keyboard ? '' : 'empty'} ${
          guide.keyboard && guide.hintActive ? 'hint' : ''
        }`}
      >
        {guide.keyboard ? (
          <div className="keyboard-stack">
            <KeyboardGuide nextKeys={play?.nextKeys ?? []} />
            {/* キーボードの上に重ねる半透明の手(参考画像準拠)。 */}
            <HandsOverlay nextKeys={play?.nextKeys ?? []} />
          </div>
        ) : null}
      </section>

      {/* パルの応援(docs/07 §7.4)。押せないただの吹き出しなので操作の邪魔をしない。 */}
      {cheer ? (
        <div className="play-cheer">
          <TutorBubble reply={{ message: cheer, mood: 'happy', tips: [], isAi: false }} size="sm" />
        </div>
      ) : null}

      {countdown > 0 ? (
        <div className="countdown">
          <span key={countdown} className="countdown-number">
            {countdown}
          </span>
        </div>
      ) : null}
    </div>
  );
}
